import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UserActionsMenu from '../components/UserActionsMenu';
import TopBar from '../components/chrome/TopBar';
import { LoadingSpinner, IntroduceModal } from '../components';
import { CloseFriendMark, MirrorRoom, useCloseFriend } from '../components/closefriends';
import { Pill, C } from '../components/portalkit';
import PortalPage from '../components/profile/PortalPage';
import { profileService, friendService, applicationService } from '../services';
import messageService from '../services/messageService';
import { useUser, useAuth } from '../context';
import { isReservedHandle } from '../utils/reservedHandles';
import { brandPath, webUrl } from '../utils/brand';
import { decodePortalPage } from '../utils/portalPageModel';
import { hasProfilePhoto } from '../utils/onboardingModel';
import PhotoGate from '../components/onboarding/PhotoGate';

/**
 * PublicProfilePage — the Portal Page at the bare-root URL `heyportal.app/{handle}`, wired as the
 * catch-all `:handle` route in App.js (placed last so every other route wins by specificity).
 *
 * Profile Page v2 (design_handoff_portal_profile_editor, Oct 2 2026): the page itself is
 * `components/profile/PortalPage` — this file fetches the profile, decides the identity actions
 * for the viewer (owner · member · signed out) and owns the real behaviour behind them: open a
 * thread, send a friend request, the close-friends mark, INTRODUCE, the ⋯ menu. EDIT PAGE goes
 * to the Portal Editor at /portal/edit; the intake stepper is for first-time onboarding only.
 *
 * Reserved-handle URLs (`/login`, `/admin`) never reach this component — their routes match first —
 * but it still gates as defence in depth in case the route table drifts.
 */
export default function PublicProfilePage() {
  const navigate = useNavigate();
  // Two doors to the same page: `/{handle}`, and `/users/{userId}` for an account that has not
  // picked a handle yet (a third of real accounts). The second redirects to the first the moment
  // a handle exists — unless a host is looking at an applicant, which keeps the userId form.
  const { handle: rawHandle, userId: routeUserId } = useParams();
  const handle = (rawHandle || '').trim().toLowerCase();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('applicationId');

  const { profile: viewerProfile, walletBalance, fetchProfile } = useUser();
  const { user, logout, isAuthenticated } = useAuth();

  const [viewedProfile, setViewedProfile] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [openingThread, setOpeningThread] = useState(false);
  // { areFriends, hasPendingRequest, sentByCurrentUser, pendingRequestId, isClose }
  const [friendStatus, setFriendStatus] = useState(null);
  const [friendBusy, setFriendBusy] = useState(false);
  // The host's view of an applicant: the application sits under the page.
  const [application, setApplication] = useState(null);

  useEffect(() => {
    if (!handle && !routeUserId) return undefined;
    if (handle && isReservedHandle(handle)) { setError('Profile not found'); setIsLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      setIsLoading(true); setError(null); setApplication(null);
      const result = routeUserId
        ? await profileService.getProfileByUserId(routeUserId)
        : await profileService.getProfileByHandle(handle);
      if (cancelled) return;
      if (!result.success || !result.data) { setError('Profile not found'); setIsLoading(false); return; }
      if (routeUserId && result.data.handle && !applicationId) {
        navigate(`/${result.data.handle}`, { replace: true });
        return;
      }
      setViewedProfile(result.data);
      if (applicationId) {
        const app = await applicationService.getApplication(applicationId);
        if (!cancelled && app.success) setApplication(app.data);
      }
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [handle, routeUserId, applicationId, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !viewedProfile?.userId) return undefined;
    if (viewerProfile?.userId && viewerProfile.userId === viewedProfile.userId) return undefined;
    let cancelled = false;
    (async () => {
      const r = await friendService.getFriendshipStatus(viewedProfile.userId);
      if (!cancelled && r.success) setFriendStatus(r.data);
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, viewedProfile, viewerProfile]);

  const isOwnProfile = !!(viewerProfile?.userId && viewedProfile && viewerProfile.userId === viewedProfile.userId);
  // The owner sees their own page the way it is saved, even a Sensual & Intimate pick a visitor
  // doesn't get; the public DTO is already filtered server-side.
  const page = useMemo(() => (viewedProfile ? decodePortalPage(viewedProfile) : null), [viewedProfile]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const chrome = <TopBar user={viewerProfile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />;

  if (isLoading) {
    return (
      <div className="pk" style={{ minHeight: '100dvh', background: '#000', color: '#fff' }}>
        {chrome}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 16px' }}><LoadingSpinner label="Loading profile..." /></div>
      </div>
    );
  }

  if (error || !viewedProfile || !page) {
    return (
      <div className="pk" style={{ minHeight: '100dvh', background: '#000', color: '#fff' }}>
        {chrome}
        <main style={{ maxWidth: 560, margin: '0 auto', padding: '72px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>Profile not found</h1>
          <p style={{ margin: 0, fontSize: 13, color: C.onBlack2, overflowWrap: 'anywhere' }}>{brandPath(handle || `users/${routeUserId}`)} doesn’t lead anywhere.</p>
          <Pill variant="primary" on="black" size={40} onClick={() => navigate('/featured')} style={{ marginTop: 10 }}>BROWSE EVENTS</Pill>
        </main>
      </div>
    );
  }

  const displayName = page.name || viewedProfile.handle || handle;
  const events = Array.isArray(viewedProfile.events) ? viewedProfile.events : [];
  const going = events.filter((e) => e.relationship === 'going');
  const interested = events.filter((e) => e.relationship === 'interested');
  const hosting = events.filter((e) => e.relationship === 'hosting');
  const clubs = Array.isArray(viewedProfile.clubs) ? viewedProfile.clubs : [];

  const handleMessage = async () => {
    if (openingThread) return;
    setOpeningThread(true);
    const r = await messageService.createOrGetThread(viewedProfile.userId, null);
    setOpeningThread(false);
    if (r.success && r.data?.id) navigate(`/messages?thread=${r.data.id}`);
  };
  const handleAddFriend = async () => {
    if (!viewedProfile?.userId || friendBusy) return;
    setFriendBusy(true);
    const r = await friendService.sendFriendRequest(viewedProfile.userId);
    setFriendBusy(false);
    if (r.success) setFriendStatus({ areFriends: false, hasPendingRequest: true, sentByCurrentUser: true, pendingRequestId: null });
  };
  const back = () => { if (window.history.length > 1) navigate(-1); else navigate('/people'); };

  let actions;
  if (!isAuthenticated) {
    actions = [
      { key: 'login', label: 'LOG IN', variant: 'secondary', onClick: () => navigate('/login') },
      { key: 'signup', label: 'SIGN UP', variant: 'primary', onClick: () => navigate('/register') },
    ];
  } else if (isOwnProfile) {
    actions = [
      { key: 'edit', label: 'EDIT PAGE', variant: 'primary', onClick: () => navigate('/portal/edit') },
      { key: 'share', label: 'SHARE', variant: 'secondary' },
    ];
  } else {
    const friend = friendStatus?.areFriends
      ? { key: 'friend', node: <ProfileCloseFriendMark userId={viewedProfile.userId} isClose={friendStatus.isClose} name={displayName} /> }
      : friendStatus?.hasPendingRequest && friendStatus?.sentByCurrentUser
        ? { key: 'friend', label: 'REQUESTED', variant: 'muted', muted: true }
        : friendStatus?.hasPendingRequest && !friendStatus?.sentByCurrentUser
          ? { key: 'friend', label: 'RESPOND →', variant: 'secondary', onClick: () => navigate('/friends') }
          : { key: 'friend', label: friendBusy ? 'SENDING…' : '+ ADD FRIEND', variant: 'secondary', onClick: handleAddFriend, disabled: friendBusy };
    // New chats between people need a friendship (Nov 2026), so MESSAGE is a friend's button;
    // until then the friend request is the one thing to do.
    actions = friendStatus?.areFriends
      ? [
        { key: 'message', label: openingThread ? 'OPENING…' : 'MESSAGE', variant: 'primary', onClick: handleMessage, disabled: openingThread },
        friend,
        { key: 'intro', label: 'INTRODUCE', variant: 'secondary', onClick: () => setIntroOpen(true) },
      ]
      : [
        friend.onClick && friend.label?.includes('ADD FRIEND') ? { ...friend, variant: 'primary' } : friend,
        { key: 'intro', label: 'INTRODUCE', variant: 'secondary', onClick: () => setIntroOpen(true) },
      ];
  }

  const menu = isAuthenticated && !isOwnProfile ? (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <Pill variant="secondary" on="black" size={40} aria-label="More actions" aria-haspopup="menu" aria-expanded={actionsOpen} onClick={() => setActionsOpen((o) => !o)} style={{ width: 40, padding: 0, fontSize: 16, letterSpacing: 0 }}>&#8943;</Pill>
      <UserActionsMenu tone="portal" anchor="right" open={actionsOpen} onClose={() => setActionsOpen(false)} userId={viewedProfile.userId} displayName={displayName} onBlocked={() => navigate('/featured')} />
    </div>
  ) : null;

  return (
    <div className="pk" style={{ minHeight: '100dvh', background: '#000', color: '#fff', overflowX: 'clip' }}>
      {chrome}
      {isAuthenticated && (
        <IntroduceModal isOpen={introOpen} onClose={() => setIntroOpen(false)} target={{ userId: viewedProfile.userId, displayName, handle: viewedProfile.handle || handle, avatarUrl: viewedProfile.avatarUrl || viewedProfile.faceUrl }} />
      )}
      <PortalPage
        data={page}
        viewer={isOwnProfile ? 'self' : 'visitor'}
        chrome={isAuthenticated}
        guestlist={going}
        interested={interested}
        hosting={hosting}
        clubs={clubs}
        actions={actions}
        menu={menu}
        shareUrl={webUrl(viewedProfile.handle || handle || `users/${viewedProfile.userId}`)}
        onBack={back}
        onOpenEvent={(e) => navigate(`/events/${e.inviteCode}`)}
        onOpenClub={(c) => navigate(`/clubs/${c.clubId}`)}
        onHost={() => navigate('/host')}
        onEdit={() => navigate('/portal/edit')}
      />
      {application && <ApplicationContext application={application} />}
      {/* Your own page with no photo on it is a blank name tag — the gate asks for one before
          showing it to you. Owner only, and the rule is step 1's (`hasProfilePhoto`). */}
      {isOwnProfile && !hasProfilePhoto(viewedProfile) && (
        <PhotoGate
          onAdded={(updated) => {
            // Merge rather than replace: the gate hands back the OWN-profile DTO, which does not
            // carry the public read's extras (events, clubs, "member since").
            setViewedProfile((prev) => ({ ...prev, ...updated }));
            // And the context, or the navbar keeps showing the blank avatar.
            fetchProfile?.();
          }}
        />
      )}
    </div>
  );
}

/** What a host sees under an applicant's page: their message, the pledge, status and date. */
function ApplicationContext({ application }) {
  const facts = [
    ['PLEDGE', `$${((application.pledgeAmountCents || 0) / 100).toFixed(2)}`],
    ['STATUS', (application.status || '').replace(/_/g, ' ').toUpperCase()],
    ['APPLIED', application.createdAt ? new Date(application.createdAt).toLocaleDateString() : '—'],
  ];
  return (
    <section data-application-context style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px 48px' }}>
      <div style={{ borderRadius: 24, background: '#111', padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>APPLICATION</span>
        {application.message && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.onBlack2 }}>MESSAGE TO HOST</span>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: '#fff', borderLeft: '2px solid #FF66C4', paddingLeft: 12, fontStyle: 'italic' }}>“{application.message}”</p>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 14 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.onBlack2 }}>{k}</span>
              <span style={{ fontSize: 15, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The close-friends mark in a friend's profile header: ✓ FRIENDS (or what just happened), the
 * disco-ball toggle, and the mirror room full screen — this page is about one person, so the
 * moment gets the whole viewport.
 */
function ProfileCloseFriendMark({ userId, isClose, name }) {
  const close = useCloseFriend(userId, isClose, name);
  return (
    <>
      <span role={close.error ? 'alert' : undefined} style={{ display: 'inline-flex', alignItems: 'center', height: 46, padding: '0 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.16em', whiteSpace: 'nowrap', textTransform: 'uppercase', color: close.error || close.flash ? '#fff' : C.onBlack3 }}>
        {close.error || (close.flash ? "Close friend · They'll know" : '✓ FRIENDS')}
      </span>
      <CloseFriendMark size={46} inverted on={close.on} onToggle={close.toggle} name={name} className="focus-visible:ring-white focus-visible:ring-offset-black" />
      <MirrorRoom q={close.q} surface="fullscreen" first={close.first} />
    </>
  );
}
